import { useContext } from 'react';
import { type ComboboxOptionType, DropdownSelect, FormField, Label, VisuallyHidden } from '@zillow/constellation-10';
import { SatelliteLeadManagementSourceType } from '@zg-rentals/ts-satellite-client';
import { useAuth } from '../../AuthProvider';
import { LeadManagementContext } from '../../contexts/LeadManagementContextProvider';
import useIsLeadSourceVisible from '../../hooks/useIsLeadSourceVisible';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';
import {
  emitFilterLeadsBySourceEvent,
  FILTER_LEADS_BY_SOURCE_EVENT_TYPE_ID,
  type FilterLeadsBySourceTriggerObject,
} from '../../constants/clickstreamEvents/leadManagementTable';

const ALL_SOURCES_VALUE = 'ALL';

// Mirrors SatelliteLeadManagementSourceType.getDisplayName(); `availableSources` carries only the type.
const SOURCE_LABELS: Record<SatelliteLeadManagementSourceType, string> = {
  [SatelliteLeadManagementSourceType.STREET_EASY]: 'StreetEasy',
  [SatelliteLeadManagementSourceType.ZILLOW_RENTAL_NETWORK]: 'Zillow Rental Network',
};

const SOURCE_TRIGGER_OBJECTS: Record<SatelliteLeadManagementSourceType, FilterLeadsBySourceTriggerObject> = {
  [SatelliteLeadManagementSourceType.STREET_EASY]: 'street_easy',
  [SatelliteLeadManagementSourceType.ZILLOW_RENTAL_NETWORK]: 'zillow_rental_network',
};

type LeadManagementSourceFilterProps = {
  fullWidth?: boolean;
};

const isSourceType = (value: string): value is SatelliteLeadManagementSourceType =>
  Object.values<string>(SatelliteLeadManagementSourceType).includes(value);

export default function LeadManagementSourceFilter({ fullWidth = false }: LeadManagementSourceFilterProps) {
  const { availableSources, listingAlias, source } = useContext(LeadManagementContext);
  const { user } = useAuth();
  const isLeadSourceVisible = useIsLeadSourceVisible();
  const { updateQueryParams } = useQueryParamsNext();

  // Stays mounted while a source is selected so an empty filtered page can still be cleared.
  if (!isLeadSourceVisible && source === null) {
    return null;
  }

  const sourceOptions = Object.values(SatelliteLeadManagementSourceType)
    .filter((type) => availableSources.includes(type) || type === source)
    .map((type) => ({ label: SOURCE_LABELS[type], value: type }));
  const options = [{ label: 'All sources', value: ALL_SOURCES_VALUE }, ...sourceOptions];

  const handleOptionSelect = (option: ComboboxOptionType) => {
    if (typeof option !== 'object' || option === null || !('value' in option)) return;
    const selectedSource = isSourceType(option.value) ? option.value : null;
    emitFilterLeadsBySourceEvent(
      user,
      { listingAlias },
      selectedSource === null ? 'all' : SOURCE_TRIGGER_OBJECTS[selectedSource],
    );
    updateQueryParams({ queryParamValues: { source: selectedSource } });
  };

  return (
    <FormField
      data-testid="lead-management-source-filter"
      data-eventid={FILTER_LEADS_BY_SOURCE_EVENT_TYPE_ID}
      css={{ flexShrink: 0, width: fullWidth ? { base: '100%', md: '200px' } : '200px' }}
      label={
        <Label>
          <VisuallyHidden>Source</VisuallyHidden>
        </Label>
      }
      control={
        <DropdownSelect
          options={options}
          value={source ?? ALL_SOURCES_VALUE}
          onOptionSelect={handleOptionSelect}
          showLabelForValue
          css={{ boxShadowColor: 'border.subtle' }}
        />
      }
    />
  );
}
